/**
 * LiveLogsDrawer — real-time tail of client_logs for super admins.
 * Subscribes to postgres_changes on client_logs filtered by session_id.
 * Shown as a bottom sheet that toggles via a long-press on the
 * "Verbonden" badge in the AdminControlPanel header.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Animated,
  Dimensions,
  Platform,
  Alert,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";

const DRAWER_HEIGHT = Dimensions.get("window").height * 0.45;

interface LogRow {
  id: string;
  level: string;
  source: string | null;
  message: string;
  context: Record<string, unknown>;
  created_at: string;
  user_id: string | null;
}

const LEVEL_COLORS: Record<string, string> = {
  debug: "#9CA3AF",
  info: "#60A5FA",
  warn: "#F59E0B",
  error: "#EF4444",
};

interface LiveLogsDrawerProps {
  sessionId: string;
  visible: boolean;
  onClose: () => void;
}

export const LiveLogsDrawer: React.FC<LiveLogsDrawerProps> = ({
  sessionId,
  visible,
  onClose,
}) => {
  const colors = useThemeColors();
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [levelFilter, setLevelFilter] = useState<string | null>(null);
  const slideAnim = useRef(new Animated.Value(DRAWER_HEIGHT)).current;
  const scrollRef = useRef<ScrollView>(null);

  // Animate open / close
  useEffect(() => {
    Animated.spring(slideAnim, {
      toValue: visible ? 0 : DRAWER_HEIGHT,
      useNativeDriver: true,
      tension: 65,
      friction: 11,
    }).start();
  }, [visible, slideAnim]);

  // Fetch initial batch + subscribe to realtime inserts
  useEffect(() => {
    if (!visible || !sessionId) return;

    let channelRef: ReturnType<typeof supabase.channel> | null = null;

    const init = async () => {
      const { data } = await supabase
        .from("client_logs")
        .select("id, level, source, message, context, created_at, user_id")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: false })
        .limit(200);

      if (data) setLogs((data as LogRow[]).reverse());

      channelRef = supabase
        .channel(`live-logs:${sessionId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "client_logs",
            filter: `session_id=eq.${sessionId}`,
          },
          (payload) => {
            const row = payload.new as LogRow;
            setLogs(prev => [...prev.slice(-499), row]);
            setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);
          },
        )
        .subscribe();
    };

    init();

    return () => {
      if (channelRef) supabase.removeChannel(channelRef);
    };
  }, [visible, sessionId]);

  const handleCopyAll = useCallback(() => {
    const text = logs
      .map(l => `[${l.level}] ${l.source || ""}: ${l.message} ${JSON.stringify(l.context)}`)
      .join("\n");
    if (Platform.OS === "web" && typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      Alert.alert("Gekopieerd", "Logs zijn alleen via web te kopiëren.");
    }
  }, [logs]);

  const handleClear = useCallback(() => setLogs([]), []);

  const filtered = levelFilter ? logs.filter(l => l.level === levelFilter) : logs;

  if (!visible) return null;

  return (
    <Animated.View
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: DRAWER_HEIGHT,
        backgroundColor: colors.surface,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        borderTopWidth: 1,
        borderColor: colors.border,
        transform: [{ translateY: slideAnim }],
        zIndex: 500,
      }}
    >
      {/* Handle + toolbar */}
      <View className="flex-row items-center justify-between px-4 pt-3 pb-2">
        <View className="flex-row items-center gap-2">
          <Ionicons name="terminal" size={18} color={colors.textSecondary} />
          <Text className="font-semibold" style={{ color: colors.text }}>
            Live Logs
          </Text>
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            ({filtered.length})
          </Text>
        </View>

        <View className="flex-row items-center gap-3">
          {/* Level filters */}
          {["debug", "info", "warn", "error"].map(lvl => (
            <TouchableOpacity
              key={lvl}
              onPress={() => setLevelFilter(prev => (prev === lvl ? null : lvl))}
              className="px-2 py-0.5 rounded"
              style={{
                backgroundColor: levelFilter === lvl ? `${LEVEL_COLORS[lvl]}30` : "transparent",
              }}
            >
              <Text
                className="text-xs font-medium uppercase"
                style={{ color: LEVEL_COLORS[lvl] }}
              >
                {lvl}
              </Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity onPress={handleCopyAll} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="copy-outline" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleClear} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="trash-outline" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close" size={20} color={colors.text} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Log rows */}
      <ScrollView
        ref={scrollRef}
        className="flex-1 px-3 pb-4"
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: false })}
      >
        {filtered.map(log => (
          <View key={log.id} className="flex-row py-0.5" style={{ gap: 6 }}>
            <Text
              className="text-xs font-mono font-medium"
              style={{ color: LEVEL_COLORS[log.level] || colors.textSecondary, width: 36 }}
            >
              {log.level.toUpperCase().slice(0, 4)}
            </Text>
            <Text
              className="text-xs font-mono"
              style={{ color: colors.textSecondary, width: 50 }}
              numberOfLines={1}
            >
              {new Date(log.created_at).toLocaleTimeString("nl-NL", { hour12: false })}
            </Text>
            <Text className="text-xs font-mono flex-1" style={{ color: colors.text }} numberOfLines={2}>
              {log.source ? `[${log.source}] ` : ""}{log.message}
            </Text>
          </View>
        ))}
      </ScrollView>
    </Animated.View>
  );
};

export default LiveLogsDrawer;
